import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useEcommerce } from '../../context/EcommerceContext';
import { useCurrency } from '../../context/CurrencyContext';
import PaymentGateway from './PaymentGateway';
import orderTrackingService from '../../services/orderTrackingService';

const {
  FiShoppingBag, FiX, FiTrash2, FiArrowRight,
  FiCheck, FiShield, FiTruck, FiGift,
  FiUser, FiMapPin, FiMessageSquare, FiCreditCard,
  FiClipboard, FiInfo, FiStar, FiHeart, FiPackage,
  FiNavigation,
} = FiIcons;

// ─── Gift Wrap Styles ──────────────────────────────────────────────────────────
const WRAP_STYLES = [
  { id: 'classic',   label: 'Classic',   emoji: '🎁', desc: 'Timeless kraft paper & ribbon' },
  { id: 'luxury',    label: 'Luxury',    emoji: '✨', desc: 'Satin box with gold foil' },
  { id: 'memorial',  label: 'Memorial',  emoji: '🕊️', desc: 'Soft ivory wrap, floral seal' },
  { id: 'seasonal',  label: 'Seasonal',  emoji: '🌿', desc: 'Eco-friendly botanical wrap' },
];

// ─── Delivery Method Picker ────────────────────────────────────────────────────
const DELIVERY_METHODS = [
  { id: 'standard',  label: 'Standard',  emoji: '📦', desc: '2-3 business days',  etaDays: 3 },
  { id: 'express',   label: 'Express',   emoji: '⚡', desc: 'Next business day',   etaDays: 1 },
  { id: 'same_day',  label: 'Same-Day',  emoji: '🚚', desc: 'Major metros only',   etaDays: 0 },
  { id: 'funeral',   label: 'Funeral Home Drop', emoji: '🏛️', desc: 'Direct to funeral home', etaDays: 2 },
  { id: 'pickup',    label: 'In-Store Pickup',   emoji: '🛍️', desc: 'Collect at your convenience', etaDays: 0 },
];

const DeliveryMethodPicker = ({ carrierId, onChange }) => (
  <div className="bg-brand-light rounded-[32px] p-6 border border-brand-dark">
    <div className="flex items-center gap-3 mb-4">
      <div className="w-10 h-10 rounded-xl bg-white text-accent-navy flex items-center justify-center shadow-sm">
        <SafeIcon icon={FiTruck} />
      </div>
      <div>
        <h4 className="text-sm font-bold text-accent-navy">Delivery Method</h4>
        <p className="text-[9px] font-black uppercase tracking-widest text-text-muted">
          Choose how you want it delivered
        </p>
      </div>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {DELIVERY_METHODS.map(method => (
        <button
          key={method.id}
          onClick={() => onChange(method.id)}
          className={`relative p-3 rounded-2xl border-2 text-left transition-all ${
            carrierId === method.id
              ? 'border-accent-gold bg-accent-gold/5 shadow-md'
              : 'border-brand-dark bg-white hover:border-accent-gold/40'
          }`}
        >
          {carrierId === method.id && (
            <span className="absolute top-2 right-2 w-4 h-4 bg-accent-gold rounded-full flex items-center justify-center">
              <SafeIcon icon={FiCheck} className="w-2.5 h-2.5 text-white" />
            </span>
          )}
          <div className="flex items-center gap-2 mb-1">
            <span className="text-lg">{method.emoji}</span>
            <span className="text-[10px] font-black text-accent-navy">{method.label}</span>
          </div>
          <p className="text-[9px] text-text-muted font-light leading-tight">{method.desc}</p>
        </button>
      ))}
    </div>
  </div>
);

// ─── Special Instructions ──────────────────────────────────────────────────────
const SpecialInstructions = ({ value, onChange }) => (
  <div className="bg-brand-light rounded-[32px] p-6 border border-brand-dark">
    <div className="flex items-center gap-3 mb-4">
      <div className="w-10 h-10 rounded-xl bg-white text-accent-navy flex items-center justify-center shadow-sm">
        <SafeIcon icon={FiClipboard} />
      </div>
      <div>
        <h4 className="text-sm font-bold text-accent-navy">Special Instructions</h4>
        <p className="text-[9px] font-black uppercase tracking-widest text-text-muted">
          Optional notes for your order
        </p>
      </div>
    </div>
    <div className="relative">
      <textarea
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder="e.g. Handle with care, deliver between 9am–5pm, engraving details…"
        rows={3}
        maxLength={500}
        className="w-full px-4 py-3 bg-white border-2 border-brand-dark rounded-2xl text-xs outline-none focus:border-accent-gold transition-colors resize-none leading-relaxed text-text-main placeholder:text-text-muted/60"
      />
      <div className="flex items-center justify-between mt-2">
        <div className="flex items-center gap-1.5 text-[9px] text-text-muted font-light">
          <SafeIcon icon={FiInfo} className="w-3 h-3 text-accent-gold" />
          Attached to your order confirmation.
        </div>
        <span className={`text-[9px] font-mono tabular-nums ${value.length >= 450 ? 'text-amber-500' : 'text-text-muted'}`}>
          {value.length}/500
        </span>
      </div>
    </div>
  </div>
);

// ─── Gift Options Panel ────────────────────────────────────────────────────────
const GiftOptionsPanel = ({ giftOptions, onChange }) => {
  const { giftWrap, wrapStyle, giftMessage, recipientName, address } = giftOptions;

  const set = (key, val) => onChange({ ...giftOptions, [key]: val });

  return (
    <div className="bg-brand-light rounded-[32px] p-6 border border-brand-dark space-y-5">
      {/* Header toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300 ${giftWrap ? 'bg-accent-gold text-white shadow-lg shadow-accent-gold/30' : 'bg-white text-accent-navy'}`}>
            <SafeIcon icon={FiGift} />
          </div>
          <div>
            <h4 className="text-sm font-bold text-accent-navy">Gift Options</h4>
            <p className="text-[9px] font-black uppercase tracking-widest text-text-muted">
              Wrap &amp; personalise this order
            </p>
          </div>
        </div>
        <button
          onClick={() => set('giftWrap', !giftWrap)}
          className={`w-12 h-6 rounded-full p-1 transition-colors duration-300 ${giftWrap ? 'bg-accent-gold' : 'bg-brand-dark'}`}
        >
          <motion.div animate={{ x: giftWrap ? 24 : 0 }} transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            className="w-4 h-4 bg-white rounded-full shadow-sm" />
        </button>
      </div>

      <AnimatePresence>
        {giftWrap && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden space-y-5 pt-4 border-t border-brand-dark/20"
          >
            {/* Wrap Style Picker */}
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-accent-navy mb-3">
                Choose Wrap Style
              </p>
              <div className="grid grid-cols-2 gap-2">
                {WRAP_STYLES.map(style => (
                  <button
                    key={style.id}
                    onClick={() => set('wrapStyle', style.id)}
                    className={`relative p-3 rounded-2xl border-2 text-left transition-all ${
                      wrapStyle === style.id
                        ? 'border-accent-gold bg-accent-gold/5 shadow-md'
                        : 'border-brand-dark bg-white hover:border-accent-gold/40'
                    }`}
                  >
                    {wrapStyle === style.id && (
                      <span className="absolute top-2 right-2 w-4 h-4 bg-accent-gold rounded-full flex items-center justify-center">
                        <SafeIcon icon={FiCheck} className="w-2.5 h-2.5 text-white" />
                      </span>
                    )}
                    <span className="text-xl block mb-1">{style.emoji}</span>
                    <p className="text-[10px] font-black text-accent-navy">{style.label}</p>
                    <p className="text-[9px] text-text-muted font-light leading-tight">{style.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Gift Message */}
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy mb-2 block flex items-center gap-1.5">
                <SafeIcon icon={FiHeart} className="w-3 h-3 text-rose-400" />
                Personal Gift Message
              </label>
              <textarea
                value={giftMessage}
                onChange={e => set('giftMessage', e.target.value)}
                placeholder="Write a heartfelt message to be included with the gift…"
                rows={3}
                maxLength={300}
                className="w-full px-4 py-3 bg-white border-2 border-brand-dark rounded-2xl text-xs outline-none focus:border-accent-gold transition-colors resize-none leading-relaxed placeholder:text-text-muted/60"
              />
              <div className="flex justify-between mt-1.5">
                <p className="text-[9px] text-text-muted font-light">Printed on a premium gift card.</p>
                <span className={`text-[9px] font-mono tabular-nums ${giftMessage.length >= 270 ? 'text-amber-500' : 'text-text-muted'}`}>
                  {giftMessage.length}/300
                </span>
              </div>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-brand-dark/30" />
              <p className="text-[9px] font-black uppercase tracking-widest text-text-muted">Ship To</p>
              <div className="flex-1 h-px bg-brand-dark/30" />
            </div>

            {/* Recipient Name */}
            <div className="relative">
              <SafeIcon icon={FiUser} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted w-3.5 h-3.5" />
              <input
                type="text"
                value={recipientName}
                onChange={e => set('recipientName', e.target.value)}
                placeholder="Recipient's Full Name *"
                className="w-full pl-10 pr-4 py-3 bg-white border-2 border-brand-dark rounded-2xl text-xs outline-none focus:border-accent-gold transition-colors"
              />
            </div>

            {/* Delivery Address */}
            <div className="relative">
              <SafeIcon icon={FiMapPin} className="absolute left-4 top-3.5 text-text-muted w-3.5 h-3.5" />
              <textarea
                value={address}
                onChange={e => set('address', e.target.value)}
                placeholder="Delivery address *"
                rows={2}
                className="w-full pl-10 pr-4 py-3 bg-white border-2 border-brand-dark rounded-2xl text-xs outline-none focus:border-accent-gold transition-colors resize-none"
              />
            </div>

            {/* Validation hint */}
            {(!recipientName.trim() || !address.trim()) && (
              <p className="text-[9px] text-amber-600 flex items-center gap-1.5">
                <SafeIcon icon={FiInfo} className="w-3 h-3" />
                Recipient name &amp; address are required to place a gift order.
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── Gift Summary Pill ─────────────────────────────────────────────────────────
const GiftSummaryPill = ({ giftOptions }) => {
  const { giftWrap, wrapStyle, recipientName } = giftOptions;
  if (!giftWrap) return null;
  const style = WRAP_STYLES.find(s => s.id === wrapStyle);
  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      className="overflow-hidden mb-4"
    >
      <div className="flex items-center gap-2 px-4 py-3 bg-accent-gold/10 border border-accent-gold/30 rounded-2xl">
        <span className="text-lg">{style?.emoji || '🎁'}</span>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-black uppercase tracking-widest text-accent-gold">
            {style?.label || 'Gift'} Wrap Selected
          </p>
          {recipientName && (
            <p className="text-[9px] text-text-muted truncate">For: {recipientName}</p>
          )}
        </div>
        <SafeIcon icon={FiCheck} className="w-3.5 h-3.5 text-accent-gold shrink-0" />
      </div>
    </motion.div>
  );
};

// ─── Order Success Detail ──────────────────────────────────────────────────────
const GiftSuccessDetail = ({ giftOptions }) => {
  const { giftWrap, wrapStyle, giftMessage, recipientName } = giftOptions;
  if (!giftWrap) return null;
  const style = WRAP_STYLES.find(s => s.id === wrapStyle);
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="w-full p-4 bg-accent-gold/10 border border-accent-gold/30 rounded-2xl text-left space-y-2">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-base">{style?.emoji || '🎁'}</span>
        <p className="text-[9px] font-black uppercase tracking-widest text-accent-gold">
          Gift Order — {style?.label} Wrap
        </p>
      </div>
      <p className="text-[10px] text-accent-navy font-bold">To: {recipientName}</p>
      {giftMessage && (
        <p className="text-xs text-text-muted italic leading-relaxed border-t border-accent-gold/20 pt-2">
          "{giftMessage}"
        </p>
      )}
    </motion.div>
  );
};

// ─── Tracking Info on Success ─────────────────────────────────────────────────
const TrackingSuccessInfo = ({ order }) => {
  if (!order) return null;
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="w-full mt-3 space-y-3">
      {/* Order ID */}
      <div className="flex items-center justify-between p-3 bg-white border border-brand-dark rounded-2xl">
        <span className="text-[10px] font-black uppercase tracking-widest text-text-muted">Order ID</span>
        <span className="text-xs font-mono font-bold text-accent-navy">{order.orderId}</span>
      </div>
      {/* Tracking number */}
      <div className="flex items-center justify-between p-3 bg-white border border-brand-dark rounded-2xl">
        <div className="flex items-center gap-2">
          <SafeIcon icon={FiNavigation} className="w-3.5 h-3.5 text-accent-gold" />
          <span className="text-[10px] font-black uppercase tracking-widest text-text-muted">Tracking #</span>
        </div>
        <span className="text-xs font-mono font-bold text-accent-navy">{order.delivery.trackingNumber}</span>
      </div>
      {/* Carrier + ETA */}
      <div className="flex items-center gap-3 p-4 bg-brand-light rounded-2xl border border-brand-dark text-left">
        <SafeIcon icon={FiTruck} className="text-accent-gold shrink-0" />
        <div className="flex-1">
          <p className="text-[10px] font-black uppercase text-accent-navy">{order.delivery.carrierLabel}</p>
          <p className="text-xs text-text-muted">
            ETA: {new Date(order.delivery.estimatedDelivery).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
          </p>
        </div>
      </div>
      {/* Link to tracking page */}
      <a href="/orders"
        className="w-full flex items-center justify-center gap-2 py-3 bg-accent-navy text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-navy/90 transition-all">
        <SafeIcon icon={FiPackage} className="w-3.5 h-3.5" /> Track Your Order
      </a>
    </motion.div>
  );
};

// ─── Cart Drawer ───────────────────────────────────────────────────────────────
const CartDrawer = ({ isOpen, onClose }) => {
  const { cart, removeFromCart, clearCart } = useEcommerce();
  const { formatCurrency, currency } = useCurrency();

  const [giftOptions, setGiftOptions] = useState({
    giftWrap: false,
    wrapStyle: 'classic',
    giftMessage: '',
    recipientName: '',
    address: '',
  });
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [carrierId, setCarrierId] = useState('standard');
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(null);
  const [createdOrder, setCreatedOrder] = useState(null);

  const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

  const canCheckout = !giftOptions.giftWrap ||
    (giftOptions.recipientName.trim() && giftOptions.address.trim());

  const handleCheckout = () => {
    if (!canCheckout) return;
    setIsPaymentOpen(true);
  };

  const handlePaymentSuccess = (result) => {
    setIsPaymentOpen(false);

    // Persist the order for delivery tracking BEFORE clearing the cart
    const order = orderTrackingService.createOrder({
      items: cart.map(item => ({
        id: item.id,
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        image: item.image,
        category: item.category,
        selectedVariations: item.selectedVariations || [],
        merchantId: item.merchantId || item.vendor_id || '',
      })),
      total: subtotal,
      currency,
      paymentResult: result,
      giftOptions: { ...giftOptions },
      specialInstructions,
      carrierId,
    });

    setCreatedOrder(order);
    setOrderSuccess({ ...result, specialInstructions, giftOptions: { ...giftOptions } });

    setTimeout(() => {
      clearCart();
      setOrderSuccess(null);
      setCreatedOrder(null);
      setGiftOptions({ giftWrap: false, wrapStyle: 'classic', giftMessage: '', recipientName: '', address: '' });
      setSpecialInstructions('');
      setCarrierId('standard');
      onClose();
    }, 8000);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-[100] flex justify-end">
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-accent-navy/40 backdrop-blur-sm"
        />

        <motion.div
          initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="relative w-full max-w-md bg-white h-full shadow-2xl flex flex-col"
        >
          {/* Header */}
          <div className="p-8 border-b border-brand-light flex justify-between items-center bg-white shrink-0">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${
                orderSuccess
                  ? 'bg-green-100 text-green-600'
                  : giftOptions.giftWrap
                  ? 'bg-accent-gold text-white'
                  : 'bg-brand-light text-accent-navy'
              }`}>
                <SafeIcon icon={orderSuccess ? FiCheck : giftOptions.giftWrap ? FiGift : FiShoppingBag} />
              </div>
              <div>
                <h3 className="text-xl font-serif text-accent-navy">
                  {orderSuccess ? 'Order Confirmed!' : giftOptions.giftWrap ? 'Gift Order' : 'Your Cart'}
                </h3>
                <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">
                  {orderSuccess
                    ? `via ${orderSuccess.gateway}`
                    : giftOptions.giftWrap
                    ? 'Wrapped with care'
                    : `${cart.length} legacy items`}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-brand-light rounded-full text-text-muted transition-colors">
              <SafeIcon icon={FiX} className="w-6 h-6" />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="flex-grow overflow-y-auto p-8 custom-scrollbar">
            <AnimatePresence mode="wait">
              {/* ── Order Success ── */}
              {orderSuccess ? (
                <motion.div key="success"
                  initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                  className="flex flex-col items-center justify-center h-full text-center py-8"
                >
                  <div className="w-24 h-24 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-6 shadow-inner">
                    <SafeIcon icon={FiCheck} className="w-12 h-12" />
                  </div>
                  <h4 className="text-3xl font-serif text-accent-navy mb-3">
                    {orderSuccess.giftOptions?.giftWrap ? 'Gift Dispatched 🎁' : 'Legacy Secured'}
                  </h4>
                  <p className="text-sm text-text-muted font-light leading-relaxed mb-4">
                    {orderSuccess.giftOptions?.giftWrap
                      ? `Your gift for ${orderSuccess.giftOptions.recipientName} has been processed.`
                      : 'Your tribute items have been ordered.'}
                  </p>
                  <div className="text-[10px] text-text-muted bg-brand-light px-4 py-2 rounded-full font-mono mb-5">
                    Ref: {orderSuccess.transactionId}
                  </div>

                  {/* Gift detail */}
                  <GiftSuccessDetail giftOptions={orderSuccess.giftOptions} />

                  {/* Special Instructions */}
                  {orderSuccess.specialInstructions && (
                    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                      className="w-full mt-3 p-4 bg-brand-light border border-brand-dark rounded-2xl text-left">
                      <div className="flex items-center gap-2 mb-2">
                        <SafeIcon icon={FiClipboard} className="text-accent-gold w-3.5 h-3.5" />
                        <p className="text-[9px] font-black uppercase tracking-widest text-accent-gold">Special Instructions</p>
                      </div>
                      <p className="text-xs text-text-main italic leading-relaxed">
                        "{orderSuccess.specialInstructions}"
                      </p>
                    </motion.div>
                  )}

                  {/* Delivery Status */}
                  <div className="w-full mt-4 flex items-center gap-3 p-4 bg-brand-light rounded-2xl border border-brand-dark text-left">
                    <SafeIcon icon={FiTruck} className="text-accent-gold" />
                    <div>
                      <p className="text-[10px] font-black uppercase text-accent-navy">Delivery Status</p>
                      <p className="text-xs text-text-muted">Order placed · tracking available</p>
                    </div>
                  </div>

                  {/* Tracking info */}
                  <TrackingSuccessInfo order={createdOrder} />
                </motion.div>
              ) : (
                /* ── Cart Items ── */
                <motion.div key="cart-items"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="space-y-6"
                >
                  {cart.length === 0 ? (
                    <div className="text-center py-20">
                      <SafeIcon icon={FiShoppingBag} className="w-12 h-12 text-text-muted/20 mx-auto mb-4" />
                      <p className="text-sm text-text-muted font-light italic">Your cart is empty.</p>
                    </div>
                  ) : (
                    <>
                      {/* Cart Items */}
                      <div className="space-y-4">
                        {cart.map(item => (
                          <div key={item.id}
                            className="flex gap-4 p-4 bg-brand-light rounded-3xl border border-brand-dark group hover:border-accent-gold/50 transition-colors">
                            <img src={item.image} className="w-20 h-20 rounded-2xl object-cover shadow-sm" alt={item.name} />
                            <div className="flex-grow">
                              <div className="flex justify-between items-start">
                                <h4 className="text-sm font-bold text-accent-navy">{item.name}</h4>
                                <button onClick={() => removeFromCart(item.id)}
                                  className="text-text-muted hover:text-red-500 transition-colors p-1">
                                  <SafeIcon icon={FiTrash2} />
                                </button>
                              </div>
                              <p className="text-[10px] text-text-muted font-black uppercase tracking-widest mt-1">
                                Qty: {item.quantity}
                              </p>
                              <p className="text-sm font-serif text-accent-gold mt-2">
                                {formatCurrency(item.price * item.quantity)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Delivery Method */}
                      <DeliveryMethodPicker carrierId={carrierId} onChange={setCarrierId} />

                      {/* Gift Options */}
                      <GiftOptionsPanel giftOptions={giftOptions} onChange={setGiftOptions} />

                      {/* Special Instructions */}
                      <SpecialInstructions value={specialInstructions} onChange={setSpecialInstructions} />
                    </>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Footer */}
          {!orderSuccess && cart.length > 0 && (
            <div className="p-8 border-t border-brand-light bg-brand-light/30 shrink-0">
              {/* Gift / Instructions summary pills */}
              <AnimatePresence>
                <GiftSummaryPill giftOptions={giftOptions} />
                {specialInstructions.trim() && (
                  <motion.div
                    key="instr-pill"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden mb-4"
                  >
                    <div className="flex items-start gap-2 px-4 py-3 bg-white border border-brand-dark rounded-2xl">
                      <SafeIcon icon={FiClipboard} className="text-accent-gold w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <p className="text-[10px] text-accent-navy leading-relaxed line-clamp-2 italic">
                        "{specialInstructions}"
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Totals */}
              <div className="space-y-2 mb-6">
                <div className="flex justify-between text-sm text-text-muted">
                  <span>Shipping</span>
                  <span className="font-bold">Calculated at next step</span>
                </div>
                {giftOptions.giftWrap && (
                  <div className="flex justify-between text-sm text-accent-gold">
                    <span className="flex items-center gap-1.5">
                      <SafeIcon icon={FiGift} className="w-3.5 h-3.5" /> Gift Wrap
                    </span>
                    <span className="font-bold">Complimentary</span>
                  </div>
                )}
                <div className="flex justify-between text-xl font-serif text-accent-navy border-t border-brand-dark pt-2 mt-2">
                  <span>Total</span>
                  <span className="text-accent-gold">{formatCurrency(subtotal)}</span>
                </div>
              </div>

              {/* Payment badges */}
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <span className="text-[9px] font-black uppercase tracking-widest text-text-muted">Pay via:</span>
                <span className="px-3 py-1 bg-[#635BFF]/10 text-[#635BFF] rounded-full text-[9px] font-black border border-[#635BFF]/20">Stripe</span>
                <span className="px-3 py-1 bg-[#00C3F7]/10 text-[#0099BB] rounded-full text-[9px] font-black border border-[#00C3F7]/20">Paystack</span>
              </div>

              <button
                onClick={handleCheckout}
                disabled={!canCheckout}
                className="w-full btn-primary py-5 text-[10px] font-black uppercase tracking-[0.2em] flex items-center justify-center gap-3 shadow-xl hover:shadow-accent-navy/20 active:scale-[0.98] transition-all disabled:opacity-50"
              >
                <SafeIcon icon={giftOptions.giftWrap ? FiGift : FiCreditCard} />
                {giftOptions.giftWrap ? 'Place Gift Order' : 'Proceed to Payment'}
                <SafeIcon icon={FiArrowRight} />
              </button>

              <div className="mt-4 flex items-center justify-center gap-2 text-[9px] font-black uppercase tracking-widest text-text-muted opacity-60">
                <SafeIcon icon={FiShield} /> Secure Encrypted Transaction
              </div>

              <button onClick={clearCart}
                className="w-full mt-4 text-[10px] font-black uppercase text-text-muted hover:text-red-500 transition-colors">
                Clear All Items
              </button>
            </div>
          )}
        </motion.div>
      </div>

      {/* Payment Gateway Modal */}
      <PaymentGateway
        isOpen={isPaymentOpen}
        onClose={() => setIsPaymentOpen(false)}
        amount={subtotal}
        currency={currency}
        specialInstructions={specialInstructions}
        giftOptions={giftOptions}
        onPaymentSuccess={handlePaymentSuccess}
      />
    </>
  );
};

export default CartDrawer;